import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import './Form.css';

export type SearchAddItem = { id: string; title: string; sub?: string | null };

/**
 * A search box that adds with one click: type part of a name (or code), pick
 * a match, and it's added straight away. The box stays open for the next one.
 * With `actionLabel` (e.g. "Choose") it doubles as a searchable picker.
 */
export function SearchAdd({
  label,
  placeholder,
  items,
  load,
  onAdd,
  noun = 'match',
  actionLabel = '+ Add',
  busyLabel = 'Adding…',
  autoFocus,
  inline,
}: {
  label: string;
  placeholder: string;
  /** Everything that could be added (already-added ones left out)... */
  items?: SearchAddItem[];
  /** ...or a search that runs as they type, for long lists (organisations). */
  load?: (q: string) => Promise<SearchAddItem[]>;
  onAdd: (item: SearchAddItem) => Promise<unknown>;
  /** Used in "No … matches". */
  noun?: string;
  /** Shown on the highlighted match. */
  actionLabel?: string;
  busyLabel?: string;
  autoFocus?: boolean;
  /** Results sit in the flow instead of floating: for dialogs, which clip a floating list. */
  inline?: boolean;
}) {
  const id = useId();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const needle = q.trim().toLowerCase();
  const [loaded, setLoaded] = useState<SearchAddItem[]>([]);
  const [loading, setLoading] = useState(false);
  const matches = load ? loaded.slice(0, 8) : (items ?? []).filter((i) => !needle || `${i.title} ${i.sub ?? ''}`.toLowerCase().includes(needle)).slice(0, 8);

  // Server search: wait for a pause in typing, and ignore answers to older queries.
  useEffect(() => {
    if (!load || !open) return;
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      load(q.trim())
        .then((r) => live && setLoaded(r))
        .catch(() => live && setLoaded([]))
        .finally(() => live && setLoading(false));
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [load, q, open]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const add = async (item: SearchAddItem) => {
    if (busy) return;
    setBusy(item.id);
    try {
      await onAdd(item);
      setQ('');
      setLoaded((prev) => prev.filter((x) => x.id !== item.id));
    } finally {
      setBusy(null);
      inputRef.current?.focus();
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && matches[active]) add(matches[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const optionId = (i: number) => `${id}-opt-${i}`;
  const showPanel = open && (matches.length > 0 || needle.length > 0 || !!load);

  return (
    <div className={`field search-add${inline ? ' is-inline' : ''}`} ref={rootRef}>
      <label htmlFor={`${id}-input`}>{label}</label>
      <div className="search-add-box">
        <svg className="search-add-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          id={`${id}-input`}
          className="search-add-input"
          placeholder={placeholder}
          value={q}
          autoComplete="off"
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          autoFocus={autoFocus}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={`${id}-list`}
          aria-activedescendant={showPanel && matches[active] ? optionId(active) : undefined}
        />
      </div>
      {showPanel ? (
        <ul className="combo-panel search-add-list" id={`${id}-list`} role="listbox" aria-label={label}>
          {matches.length === 0 ? (
            <li className="combo-empty">
              {loading ? (
                'Searching…'
              ) : needle ? (
                <>
                  No {noun} matches &ldquo;{q.trim()}&rdquo;.
                </>
              ) : (
                `No ${noun}s left to add.`
              )}
            </li>
          ) : null}
          {matches.map((m, i) => (
            <li
              key={m.id}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={`combo-option search-add-option${i === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(m)}
            >
              <span className="search-add-text">
                <span className="search-add-title">{m.title}</span>
                {m.sub ? <span className="search-add-sub">{m.sub}</span> : null}
              </span>
              <span className="search-add-action" aria-hidden="true">
                {busy === m.id ? busyLabel : actionLabel}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
